import { useAtomValue } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { queriesEnabledAtom } from "../../../atoms/GlobalAtoms";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import TenantAPI from "../../api/TenantAPI";
import { getErrorMessage } from "../../../utils/GeneralUtils";

/**
 * useTags - Hook to get all tags. Uses react-query to cache results.
 */
const useTagsAll = (moduleAPIName: string, disabled = false) => {
  const queriesEnabled = useAtomValue(queriesEnabledAtom);
  const workspaceID = useAtomValue(workspaceIdAtom);

  const tags = useQuery({
    queryKey: ["all-tags", workspaceID],
    queryFn: () =>
      TenantAPI.getAllTags(workspaceID).then((data) => data.data.data.tags),
    enabled: queriesEnabled,
    staleTime: Infinity,
  });

  const tagsRef = useRef<any>();
  tagsRef.current = tags;

  const finalTags = useMemo(() => {
    if (!moduleAPIName) return tagsRef.current.data;

    return tagsRef.current.data?.filter((t: any) => {
      if (
        t?.Scope?.Type === "Module" &&
        t?.Scope?.ModuleAPIName !== moduleAPIName
      ) {
        return false;
      }

      return true;
    });
  }, [JSON.stringify(tags.data), moduleAPIName]);

  // If a moduleAPIName is provided, filter the tags to only those that belong to that module.
  return {
    tags: finalTags,
    tagsLoading: tags.isLoading,
    tagsError: tags.error ? getErrorMessage(tags.error) : null,
  };
};

export default useTagsAll;
